import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.js";
import { AsOfBadge, ErrorBanner, InfoTooltip, Kpi, Panel, pct } from "../components/Common.js";

interface AccumulatorResponse {
  runId: string;
  modelVersion: string;
  seed: number;
  pathCount: number;
  valuationDate: string;
  spot: number;
  rate: number;
  dividendYield: number;
  surfaceAsOf: string | null;
  marketDataWarnings: { ticker: string; code: string; message: string }[];
  result: {
    fairStrikePct: number;
    fairStrikeAbsolute: number;
    standardError: number;
    converged: boolean;
    probabilityKnockedOut: number;
    probabilitySurvivesToMaturity: number;
    expectedNormalShareDeliveries: number;
    expectedLeveragedShareDeliveries: number;
    expectedLifeYears: number;
    pathCount: number;
  };
}

export function AccumulatorPage() {
  const [productType, setProductType] = useState<"accumulator" | "decumulator">("accumulator");
  const [ticker, setTicker] = useState("AAPL");
  const [tenorMonths, setTenorMonths] = useState(6);
  const [guaranteedDays, setGuaranteedDays] = useState(20);
  const [koLevelPct, setKoLevelPct] = useState(1.15);
  const [leverage, setLeverage] = useState(2);
  const [pricingQuality, setPricingQuality] = useState<"fast" | "standard" | "high-precision">("standard");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<AccumulatorResponse>("/pricing/accumulator", {
        ticker,
        productType,
        valuationDate: new Date().toISOString().slice(0, 10),
        tenorMonths,
        guaranteedDays,
        koLevelPct,
        leverage,
        baseShares: 1,
        deliveryFrequency: "daily",
        koObservationFrequency: "daily",
        settlementLagDays: 2,
        pricingQuality,
      }),
  });

  return (
    <div className="space-y-3">
      <Panel title="Contractual inputs">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <div>
            <label className="field-label">Product</label>
            <select className="field-input" value={productType} onChange={(e) => setProductType(e.target.value as never)}>
              <option value="accumulator">Accumulator</option>
              <option value="decumulator">Decumulator</option>
            </select>
          </div>
          <div>
            <label className="field-label">Ticker</label>
            <input className="field-input" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className="field-label">Tenor (months)</label>
            <input type="number" className="field-input" value={tenorMonths} onChange={(e) => setTenorMonths(Number(e.target.value))} />
          </div>
          <div>
            <label className="field-label">
              Guaranteed days<InfoTooltip text="Minimum number of accumulation/delivery days guaranteed regardless of KO." />
            </label>
            <input type="number" className="field-input" value={guaranteedDays} onChange={(e) => setGuaranteedDays(Number(e.target.value))} />
          </div>
          <div>
            <label className="field-label">
              KO level (% spot)<InfoTooltip text="Knockout barrier as a percentage of initial spot, e.g. 1.15 = 115%." />
            </label>
            <input type="number" step="0.01" className="field-input" value={koLevelPct} onChange={(e) => setKoLevelPct(Number(e.target.value))} />
          </div>
          <div>
            <label className="field-label">Leverage</label>
            <input type="number" step="0.1" className="field-input" value={leverage} onChange={(e) => setLeverage(Number(e.target.value))} />
          </div>
        </div>
        <button className="mt-3 text-xs text-blue-400 underline" onClick={() => setShowAdvanced((s) => !s)}>
          {showAdvanced ? "Hide" : "Show"} advanced model settings
        </button>
        {showAdvanced && (
          <div className="mt-2 grid grid-cols-3 gap-3">
            <div>
              <label className="field-label">Pricing quality</label>
              <select className="field-input" value={pricingQuality} onChange={(e) => setPricingQuality(e.target.value as never)}>
                <option value="fast">Fast (2,000 paths)</option>
                <option value="standard">Standard (8,000 paths)</option>
                <option value="high-precision">High precision (32,000 paths)</option>
              </select>
            </div>
          </div>
        )}
        <button className="btn-primary mt-3" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? "Solving for fair strike…" : "Solve fair strike"}
        </button>
      </Panel>

      {mutation.error && <ErrorBanner message={(mutation.error as Error).message} />}

      {mutation.data && (
        <>
          <Panel
            title="Market data used"
            right={<AsOfBadge asOf={mutation.data.surfaceAsOf} isStale={!mutation.data.surfaceAsOf} />}
          >
            <div className="grid grid-cols-4 gap-3 text-sm font-mono">
              <div>Spot: {mutation.data.spot.toFixed(2)}</div>
              <div>Rate: {pct(mutation.data.rate)}</div>
              <div>Dividend yield: {pct(mutation.data.dividendYield)}</div>
              <div>Model v{mutation.data.modelVersion}, seed {mutation.data.seed}</div>
            </div>
            {mutation.data.marketDataWarnings.map((w, i) => (
              <div key={i} className="mt-2 rounded border border-amber-800/50 bg-amber-950/30 px-2 py-1 text-xs text-amber-300">
                {w.message}
              </div>
            ))}
          </Panel>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi
              label="Fair strike"
              value={`${pct(mutation.data.result.fairStrikePct)} (${mutation.data.result.fairStrikeAbsolute.toFixed(2)})`}
              tooltip="Strike solved so the zero-cost OTC accumulator has zero PV at inception (see docs/payoffs.md)."
            />
            <Kpi label="P(knocked out)" value={pct(mutation.data.result.probabilityKnockedOut)} />
            <Kpi label="P(survives to maturity)" value={pct(mutation.data.result.probabilitySurvivesToMaturity)} />
            <Kpi label="Expected life" value={`${mutation.data.result.expectedLifeYears.toFixed(2)}y`} />
            <Kpi label="Expected normal shares" value={mutation.data.result.expectedNormalShareDeliveries.toFixed(3)} />
            <Kpi label="Expected leveraged shares" value={mutation.data.result.expectedLeveragedShareDeliveries.toFixed(3)} />
            <Kpi label="Std. error" value={mutation.data.result.standardError.toFixed(5)} />
            <Kpi label="Paths" value={mutation.data.result.pathCount.toLocaleString()} />
          </div>
          {!mutation.data.result.converged && (
            <ErrorBanner message="Solver did not fully converge within tolerance — treat this result as indicative only." />
          )}
        </>
      )}
    </div>
  );
}
